import type { CollectionType } from "@rarible/api-client"
import type { CollectionId, OrderId } from "@rarible/types"
import type { UnionAddress } from "../../blockchain/address"
import type { Blockchain } from "../../blockchain/types"
import type { Drop } from "../../drop/types"
import type { ListingSource } from "../../nft/listing-source"
import type { PriceValue } from "../../price/types"

/**
 * Solana NFT standards returned on `collection.type` / `nft.collection.type`
 * (plus marketplace alias `TOKEN_GROUP_2022` → treated as Token-2022 by parsers).
 */
export type SolanaCollectionNftStandard =
  | "MPL_CORE"
  | "METAPLEX"
  | "TOKEN2022"
  | "WNS"
  | "BUBBLEGUM"

export type NftCollectionType =
  | CollectionType
  | SolanaCollectionNftStandard
  | "GENERIC"

export interface SocialLinks {
  twitter?: string
  instagram?: string
  discord?: string
  site?: string
}

export interface CollectionStatistic {
  floor?: PriceValue
  dayVolume?: PriceValue
  /** Every marketplace's last 7 days; read with `showWeekVolume`. */
  weekVolume?: PriceValue
  totalVolume?: PriceValue
  /**
   * Set for a Solana collection other marketplaces trade: Magic Eden
   * publishes no all-time volume, so the header shows `weekVolume`, the
   * longest window that counts every marketplace, in place of `totalVolume`.
   */
  showWeekVolume?: boolean
  topOffer?: PriceValue
  owners: number
  listedItems: number
  items: number
  dayFloorChange: number
}

export interface Order {
  id: OrderId
  price: PriceValue
  maker: string
}

export interface CollectionScanner {
  url: string
  icon: string
}

export interface NftCollection {
  id: CollectionId
  blockchain: Blockchain
  name: string
  image?: string
  verified: boolean
  createdAt?: Date
  links: SocialLinks
  scanner?: CollectionScanner
  drop?: Drop
  showcaseListed?: boolean
  type?: NftCollectionType
}

export interface CollectionItemHolder {
  address: UnionAddress
  itemsHold: number
  itemsHoldPercentage?: number
}

export interface CollectionOfferGroup {
  offerPrice: PriceValue
  totalVolume: string
  offersCount: number
  makers:
    | { type: "single"; address: UnionAddress }
    | { type: "multiple"; count: number }
  /**
   * Absent for Rarible's own bids, which is every row from the OLAP aggregate.
   * Set on rows aggregated from another marketplace so the table can say where
   * the offer lives — taking it needs that marketplace, not us.
   */
  source?: ListingSource
}

export enum CollectionFloorPriceChangePeriod {
  HOUR = "H1",
  DAY = "D1",
  WEEK = "D7",
  MONTH = "D30",
  HALF_YEAR = "D180",
  YEAR = "D365",
}

export interface FeaturedCollection {
  id: CollectionId
  blockchain: Blockchain
  name: string
  background: string
  preview?: string
  floorPrice?: PriceValue
  verified: boolean
}

export interface FeaturedCollectionMiniature {
  id?: CollectionId
  name: string
  image: string
  floorPrice?: PriceValue
  floorPriceChange?: number
  placeholder?: boolean
  mediaType?: "image" | "video"
  video?: string
}
